import classNames from 'classnames';
import IconButton from 'components/common/IconButton';
import React, { useContext, useState } from 'react';
import { Col, Row } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import PropTypes from 'prop-types';
import { ProductContext } from 'context/Context';
import useProductHook from '../useProductHook';
import StarRating from 'components/common/StarRating';
import QuantityController from '../../QuantityController';
import { useTranslation, Trans } from 'react-i18next';
import CopyButton from '../CopyButton';

const ProductDetailsMain = ({ product }) => {
  const { t } = useTranslation('commerce');
  const {
    id,
    name,
    category,
    rating,
    totalReview,
    favorite,
    shortDescription,
    price,
    priceRemark,
    salePrice,
    discount,
    shippingCost,
    isInStock,
    stock,
    tags
  } = product;
  const {
    isInFavouriteItems,
    productsState: { config }
  } = useContext(ProductContext);
  const currency = config.currency;

  const [productCount, setProductCount] = useState(1);

  const { handleAddToCart, handleFavouriteClick } = useProductHook(product);

  const handleQuantityChange = e => {
    setProductCount(parseInt(e.target.value < 1 ? 1 : e.target.value));
  };

  const handleQuantityIncrease = () => {
    setProductCount(productCount + 1);
  };

  const handleQuantityDecrease = () => {
    productCount > 1 && setProductCount(productCount - 1);
  };

  return (
    <>
      <h5>{name}</h5>
      <Link to="#!" className="fs--1 mb-2 d-block">
        {category}
      </Link>
      <div className=" mb-3 d-inline-block">
        <StarRating readonly rating={rating} />
        {config.withReview ? (
          <span className="ms-1 text-600">({totalReview})</span>
        ) : null}
      </div>
      {shortDescription && <p className="fs--1">{shortDescription}</p>}
      <h4 className="d-flex align-items-center">
        <span className="text-warning fs-3 me-2">
          {`${currency}${salePrice ? salePrice : price}`}
        </span>
        {salePrice ? (
          <span className="me-1 fs-0 fst-italic text-500">
            <del className="me-1">{`${
              isNaN(price) ? '' : currency
            }${price}`}</del>
            <strong>{discount}</strong>
          </span>
        ) : null}
      </h4>
      {priceRemark && <p className="fs--1 mb-2">{priceRemark}</p>}
      {shippingCost ? (
        <p className="fs--1 mb-1">
          {isNaN(shippingCost) ? (
            <strong>{shippingCost}</strong>
          ) : (
            <>
              <span>
                <Trans ns="commerce">Shipping Cost</Trans> :{' '}
              </span>
              <strong> {`${currency}${shippingCost}`}</strong>
            </>
          )}
        </p>
      ) : null}
      <p className="fs--1">
        <Trans ns="commerce">Stock</Trans> :{' '}
        <strong
          className={classNames({
            'text-success': isInStock,
            'text-danger': !isInStock
          })}
        >
          {stock ? stock : isInStock ? t('Available') : t('Sold-Out')}
        </strong>
      </p>
      {tags && (
        <p className="fs--1 mb-3">
          <Trans ns="commerce">Tags</Trans> :{' '}
          {tags?.map((tag, index) => (
            <Link
              to="#!"
              key={tag}
              className={classNames({
                'ms-2': index === 0,
                'ms-1': index > 0
              })}
            >
              {tag},
            </Link>
          ))}
        </p>
      )}
      <CopyButton product={product} />
      <Row>
        <Col xs="auto" className="pe-0">
          <div className="">
            <QuantityController
              disabled={!config.withCart}
              quantity={productCount}
              handleChange={handleQuantityChange}
              handleIncrease={handleQuantityIncrease}
              handleDecrease={handleQuantityDecrease}
            />
          </div>
        </Col>
        <Col xs="auto" className="px-2 px-md-3">
          <IconButton
            disabled={!config.withCart}
            iconClassName="me-sm-2"
            variant="primary"
            size="sm"
            icon="cart-plus"
            onClick={() => handleAddToCart(productCount, true)}
          >
            <span className="d-none d-sm-inline-block">
              <Trans ns="commerce">Add to Cart</Trans>
            </span>
          </IconButton>
        </Col>
        <Col xs="auto" className="px-0">
          <IconButton
            disabled={!config.withFavorite}
            className="border-300"
            iconClassName="me-1"
            variant="outline-danger"
            size="sm"
            icon={isInFavouriteItems(id) ? 'heart' : ['far', 'heart']}
            onClick={handleFavouriteClick}
          >
            {favorite}
          </IconButton>
        </Col>
      </Row>
    </>
  );
};

ProductDetailsMain.propTypes = {
  product: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    name: PropTypes.string.isRequired,
    category: PropTypes.string.isRequired,
    price: PropTypes.number.isRequired,
    priceRemark: PropTypes.string,
    discount: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    salePrice: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    shippingCost: PropTypes.number.isRequired,
    rating: PropTypes.number.isRequired,
    totalReview: PropTypes.number.isRequired,
    isInStock: PropTypes.bool.isRequired,
    shortDescription: PropTypes.string,
    stock: PropTypes.string,
    tags: PropTypes.array,
    favorite: PropTypes.any
  })
};

export default ProductDetailsMain;
